import type { Product } from './types'
import {
  PAGES,
  pagePath,
  productFromSlug,
  type FixedTab,
  type PageLabelKey,
  type PageSlug,
  type RouteHandle,
} from './routes'

export type Crumb =
  | { kind: 'home'; to?: string }
  | { kind: 'page'; labelKey: PageLabelKey; to?: string }
  | { kind: 'tab'; product: Product }
  | { kind: 'fixedTab'; labelKey: FixedTab['labelKey'] }

/** The part of a React Router match the trail needs (structurally a
 * `UIMatch` from `useMatches()`), kept narrow so the function stays pure and
 * testable without a router. */
export interface CrumbMatch {
  params: Readonly<Record<string, string | undefined>>
  handle: unknown
}

function handleOf(match: CrumbMatch | undefined): RouteHandle | undefined {
  const handle = match?.handle
  return handle && typeof handle === 'object' ? handle : undefined
}

/** The breadcrumb trail for the matched route chain (MS-818; prototype
 * `renderBreadcrumb`): Home › Section › Tab. It reads the route handles
 * pageRoutes.tsx declares and the already-decoded `:tab` param, so the trail
 * always agrees with what React Router rendered — `/Usage/mambu-mcp` and
 * `/usage/mambu%2Dmcp` both give Home › Usage › Mambu MCP, like the page.
 *
 * - `null` on Home — no breadcrumb there, matching the prototype.
 * - The `/{page}/:tab` route with a known product → Home › Page › Tab; Page
 *   links to the bare page (which lands on its default tab — prototype 3229
 *   → 3292).
 * - A page's fixed-tab route (`/settings/access`, MS-997) → Home › Settings ›
 *   Access, Settings linking to the page (prototype `renderBreadcrumb` with
 *   `FIXED_TABS`).
 * - Any other route under a known page (a tab-less page, or the in-page
 *   not-found state under one) → Home › Page, with Page as the current crumb:
 *   a section is only a link when a tab follows it.
 * - Anything else (an unknown page's not-found state) → Home alone, as the
 *   current crumb.
 *
 * The last crumb is always the current one (not a link). */
export function crumbTrail(matches: readonly CrumbMatch[]): Crumb[] | null {
  const leaf = matches.at(-1)
  const leafHandle = handleOf(leaf)
  if (leafHandle?.home) return null
  // The innermost route that names a page wins.
  let slug: PageSlug | undefined
  for (const m of matches) slug = handleOf(m)?.page ?? slug
  if (!slug) return [{ kind: 'home' }]
  const home: Crumb = { kind: 'home', to: '/' }
  const page = PAGES[slug]
  if (leafHandle?.fixedTab && page.fixedTab) {
    return [
      home,
      { kind: 'page', labelKey: page.labelKey, to: pagePath(page.slug) },
      { kind: 'fixedTab', labelKey: page.fixedTab.labelKey },
    ]
  }
  const product = leafHandle?.tab ? productFromSlug(leaf?.params.tab) : undefined
  if (!product) return [home, { kind: 'page', labelKey: page.labelKey }]
  return [
    home,
    { kind: 'page', labelKey: page.labelKey, to: pagePath(page.slug) },
    { kind: 'tab', product },
  ]
}
